import React, { useEffect, useState } from 'react';
import { getCurrentUser, updateUserAttributes, fetchUserAttributes, fetchAuthSession } from '@aws-amplify/auth';
import axios from 'axios';
import SERVICE_API_URL from '../serviceUrl';

const theme = {
  green: '#2ecc40',
  orange: '#ff851b',
  background: '#f7fafc',
  cardBg: '#fff',
  border: '#e0e0e0',
  error: '#ff4136',
  text: '#222',
  label: '#666',
  inputBg: '#f9f9f9',
  inputBorder: '#dcdcdc',
  shadow: '0 4px 32px rgba(44,204,64,0.08)',
};

const pageBg = {
  minHeight: '100vh',
  background: `linear-gradient(135deg, ${theme.background} 60%, #fffbe6 100%)`,
  padding: '48px 0',
};

const cardStyle = {
  background: theme.cardBg,
  border: `1px solid ${theme.border}`,
  borderRadius: 20,
  boxShadow: theme.shadow,
  maxWidth: 420,
  margin: '0 auto',
  padding: '40px 32px 32px 32px',
  color: theme.text,
  position: 'relative',
};

const subCardStyle = {
  background: theme.cardBg,
  border: `1.5px solid ${theme.border}`,
  borderRadius: 16,
  boxShadow: '0 2px 12px rgba(255,133,27,0.06)',
  margin: '0 0 32px 0',
  padding: '28px 20px 20px 20px',
  color: theme.text,
};

const headerIcon = {
  fontSize: 38,
  color: theme.green,
  marginBottom: 8,
  display: 'block',
  textAlign: 'center',
};

const mainHeader = {
  color: '#111',
  fontWeight: 800,
  fontSize: 28,
  marginBottom: 4,
  textAlign: 'center',
  letterSpacing: 1,
};

const subtitle = {
  color: theme.label,
  fontSize: 16,
  textAlign: 'center',
  marginBottom: 32,
};

const sectionHeader = {
  color: '#111',
  fontWeight: 700,
  fontSize: 18,
  marginBottom: 18,
  marginTop: 0,
  letterSpacing: 0.5,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const formGroup = {
  marginBottom: 24,
  position: 'relative',
};

const floatingLabel = {
  position: 'absolute',
  left: 14,
  top: 10,
  fontSize: 15,
  color: theme.label,
  background: theme.cardBg,
  padding: '0 4px',
  pointerEvents: 'none',
  transition: 'all 0.2s',
};

const inputStyle = {
  width: '100%',
  padding: '18px 12px 8px 12px',
  borderRadius: 10,
  border: `1.5px solid ${theme.inputBorder}`,
  background: theme.inputBg,
  fontSize: 16,
  marginBottom: 0,
  marginTop: 0,
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border 0.2s',
};

const inputFocused = {
  border: `1.5px solid ${theme.green}`,
};

const greenButton = {
  background: theme.green,
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '14px 0',
  width: '100%',
  fontWeight: 700,
  fontSize: 17,
  cursor: 'pointer',
  marginTop: 8,
  marginBottom: 0,
  boxShadow: '0 2px 8px rgba(44,204,64,0.08)',
  transition: 'background 0.2s',
  letterSpacing: 0.5,
};

const buttonDisabled = {
  ...greenButton,
  background: '#b2dfdb',
  cursor: 'not-allowed',
};

const successStyle = {
  color: theme.green,
  background: '#eaffea',
  borderRadius: 8,
  padding: '10px 14px',
  marginTop: 14,
  marginBottom: 0,
  fontWeight: 500,
  fontSize: 15,
  textAlign: 'center',
};

const errorStyle = {
  color: theme.error,
  background: '#ffeaea',
  borderRadius: 8,
  padding: '10px 14px',
  marginTop: 14,
  marginBottom: 0,
  fontWeight: 500,
  fontSize: 15,
  textAlign: 'center',
};

const dividerStyle = {
  border: 0,
  borderTop: `2px dashed ${theme.orange}`,
  margin: '36px 0',
};

const headerRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 14,
  marginBottom: 4,
};

// Add phone validation helper at the top
function isValidPhone(phone) {
  // Require +91 followed by 10 digits (total 13 characters)
  return /^\+91\d{10}$/.test(phone);
}

const Settings = () => {
  const [cognitoFields, setCognitoFields] = useState({ name: '', phone: '' });
  const [ddbFields, setDdbFields] = useState({ school: '', city: '', grade: '' });
  const [loading, setLoading] = useState(true);
  const [savingCognito, setSavingCognito] = useState(false);
  const [savingDdb, setSavingDdb] = useState(false);
  const [successCognito, setSuccessCognito] = useState('');
  const [successDdb, setSuccessDdb] = useState('');
  const [errorCognito, setErrorCognito] = useState('');
  const [errorDdb, setErrorDdb] = useState('');
  const [focus, setFocus] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await getCurrentUser();
        const attributes = await fetchUserAttributes();
        setCognitoFields({
          name: attributes.given_name || '',
          phone: attributes.phone_number || ''
        });
        const session = await fetchAuthSession();
        const jwt = session.tokens?.idToken?.toString();
        if (jwt) {
          const res = await axios.get(`${SERVICE_API_URL}/prep/user-profile`, {
            headers: { Authorization: `Bearer ${jwt}` }
          });
          setDdbFields({
            school: res.data.school || '',
            city: res.data.city || '',
            grade: res.data.grade || ''
          });
        }
      } catch (err) {
        // ignore
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleCognitoChange = e => {
    setCognitoFields({ ...cognitoFields, [e.target.name]: e.target.value });
  };
  const handleDdbChange = e => {
    setDdbFields({ ...ddbFields, [e.target.name]: e.target.value });
  };
  const handleFocus = (e) => {
    setFocus(f => ({ ...f, [e.target.name]: true }));
  };
  const handleBlur = (e) => {
    setFocus(f => ({ ...f, [e.target.name]: false }));
  };

  const saveCognito = async e => {
    e.preventDefault();
    setSavingCognito(true);
    setSuccessCognito('');
    setErrorCognito('');

    // Phone validation
    if (cognitoFields.phone && !isValidPhone(cognitoFields.phone)) {
      setErrorCognito('Phone number must be in the format +91XXXXXXXXXX');
      setSavingCognito(false);
      return;
    }

    try {
      const user = await getCurrentUser();
      const attributesToUpdate = {};
      if (typeof cognitoFields.name === 'string' && cognitoFields.name.trim() !== '') {
        attributesToUpdate.given_name = cognitoFields.name;
      }
      if (typeof cognitoFields.phone === 'string' && cognitoFields.phone.trim() !== '') {
        attributesToUpdate.phone_number = cognitoFields.phone;
      }
      if (Object.keys(attributesToUpdate).length === 0) {
        setErrorCognito('Please enter a name or phone number to update.');
        setSavingCognito(false);
        return;
      }
      await updateUserAttributes({
        userAttributes: attributesToUpdate
      });
      setSuccessCognito('Basic profile updated!');
    } catch (err) {
      let message = 'Failed to update Cognito profile.';
      // Check for AWS error type in headers
      const errType = err?.response?.headers?.['x-amzn-errortype'];
      const errMsg = err?.response?.headers?.['x-amzn-errormessage']
        || err?.response?.data?.message
        || err?.response?.data?.['x-amzn-errormessage'];
      if (errType && errType.startsWith('InvalidParameterException')) {
        if (errMsg && errMsg.toLowerCase().includes('phone number')) {
          message = 'Phone number must be in the format +91XXXXXXXXXX';
        } else {
          message = errMsg || 'Invalid parameter.';
        }
      } else if (err?.response?.data?.__type === 'InvalidParameterException') {
        if (errMsg && errMsg.toLowerCase().includes('phone number')) {
          message = 'Phone number must be in the format +91XXXXXXXXXX';
        } else {
          message = errMsg || 'Invalid parameter.';
        }
      } else if (errMsg) {
        message = errMsg;
      }
      setErrorCognito(message);
      console.log('Settings: Error updating Cognito profile', err);
    }
    setSavingCognito(false);
  };

  const saveDdb = async e => {
    e.preventDefault();
    setSavingDdb(true);
    setSuccessDdb('');
    setErrorDdb('');
    try {
      await getCurrentUser(); // ensure signed in
      const session = await fetchAuthSession();
      const jwt = session.tokens?.idToken?.toString();
      if (!jwt) throw new Error('No JWT found');
      const school = (ddbFields.school || '').trim();
      const city = (ddbFields.city || '').trim();
      await axios.put(`${SERVICE_API_URL}/prep/user-profile`, {
        school,
        city,
        grade: ddbFields.grade
      }, {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      setDdbFields({ ...ddbFields, school, city });
      setSuccessDdb('Academic profile updated!');
    } catch (err) {
      setErrorDdb('Failed to update academic profile.');
      console.log('Settings: Error updating academic profile', err);
    }
    setSavingDdb(false);
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: 80 }}>Loading...</div>;

  const gradeOptions = [
    'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth'
  ];

  return (
    <div style={pageBg}>
      <div style={cardStyle}>
        <div style={headerRow}>
          <span style={headerIcon}>⚙️</span>
          <span style={mainHeader}>Settings</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          <div style={subCardStyle}>
            <form onSubmit={saveCognito} autoComplete="off">
              <div style={sectionHeader}><span>👤</span> Basic Profile</div>
              <div style={formGroup}>
                <input
                  name="name"
                  value={cognitoFields.name}
                  onChange={handleCognitoChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  style={{ ...inputStyle, ...(focus.name ? inputFocused : {}) }}
                  placeholder=" "
                  autoComplete="off"
                  maxLength={15}
                />
                <label style={{
                  ...floatingLabel,
                  top: focus.name || cognitoFields.name ? -10 : 10,
                  fontSize: focus.name || cognitoFields.name ? 13 : 15,
                  color: focus.name ? theme.green : theme.label,
                  background: theme.cardBg,
                  zIndex: 1,
                  left: 14,
                  transition: 'all 0.2s',
                }}>Name</label>
              </div>
              <div style={formGroup}>
                <input
                  name="phone"
                  value={cognitoFields.phone}
                  onChange={handleCognitoChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  style={{ ...inputStyle, ...(focus.phone ? inputFocused : {}) }}
                  placeholder=" "
                  autoComplete="off"
                />
                <label style={{
                  ...floatingLabel,
                  top: focus.phone || cognitoFields.phone ? -10 : 10,
                  fontSize: focus.phone || cognitoFields.phone ? 13 : 15,
                  color: focus.phone ? theme.green : theme.label,
                  background: theme.cardBg,
                  zIndex: 1,
                  left: 14,
                  transition: 'all 0.2s',
                }}>Phone</label>
              </div>
              <button type="submit" style={savingCognito ? buttonDisabled : greenButton} disabled={savingCognito}>Update</button>
              {successCognito && <div style={successStyle}>{successCognito}</div>}
              {errorCognito && <div style={errorStyle}>{errorCognito}</div>}
            </form>
          </div>
          <div style={subCardStyle}>
            <form onSubmit={saveDdb} autoComplete="off">
              <div style={sectionHeader}><span>🎓</span> Academic Profile</div>
              <div style={formGroup}>
                <input
                  name="school"
                  type="text"
                  value={ddbFields.school}
                  onChange={handleDdbChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  style={{ ...inputStyle, ...(focus.school ? inputFocused : {}) }}
                  placeholder=" "
                  autoComplete="off"
                />
                <label style={{
                  ...floatingLabel,
                  top: focus.school || ddbFields.school ? -10 : 10,
                  fontSize: focus.school || ddbFields.school ? 13 : 15,
                  color: focus.school ? theme.green : theme.label,
                  background: theme.cardBg,
                  zIndex: 1,
                  left: 14,
                  transition: 'all 0.2s',
                }}>School</label>
              </div>
              <div style={formGroup}>
                <input
                  name="city"
                  type="text"
                  value={ddbFields.city}
                  onChange={handleDdbChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  style={{ ...inputStyle, ...(focus.city ? inputFocused : {}) }}
                  placeholder=" "
                  autoComplete="off"
                />
                <label style={{
                  ...floatingLabel,
                  top: focus.city || ddbFields.city ? -10 : 10,
                  fontSize: focus.city || ddbFields.city ? 13 : 15,
                  color: focus.city ? theme.green : theme.label,
                  background: theme.cardBg,
                  zIndex: 1,
                  left: 14,
                  transition: 'all 0.2s',
                }}>City</label>
              </div>
              <div style={formGroup}>
                <select
                  name="grade"
                  value={ddbFields.grade}
                  onChange={handleDdbChange}
                  style={{ ...inputStyle, paddingRight: 32, appearance: 'auto' }}
                >
                  <option value="" disabled>Select grade</option>
                  {gradeOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <label style={{
                  ...floatingLabel,
                  top: ddbFields.grade ? -10 : 10,
                  fontSize: ddbFields.grade ? 13 : 15,
                  color: focus.grade ? theme.green : theme.label,
                  background: theme.cardBg,
                  zIndex: 1,
                  left: 14,
                  transition: 'all 0.2s',
                }}>Grade</label>
              </div>
              <button type="submit" style={savingDdb ? buttonDisabled : greenButton} disabled={savingDdb}>Update</button>
              {successDdb && <div style={successStyle}>{successDdb}</div>}
              {errorDdb && <div style={errorStyle}>{errorDdb}</div>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 